import { setNestedObjectValues, useFormik } from "formik";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDealPortfolioStore, useDrawMapStore, useMapMarkerStore, usePipedriveStore } from "../..";
import useClosable from "../../hooks/useClosable";
import useDealDelete from "../../hooks/useDealDelete";
import useDealUpdate from "../../hooks/useDealUpdate";
import useValidateDeal from "../../hooks/useValidateDeal";
import DetailsType from "../../interfaces/DetailsType";
import { GeoCoordinate } from "../../interfaces/GeoCoordinate";
import { DealState, IDealState } from "../../interfaces/IDeal";
import { ClosableOrder } from "../../store/ClosableStore";
import { formatPrice } from "../../utils/utils";
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import DetailsPanel from "../DetailsPanel";
import EditControls, { DeleteControl } from "../EditControls";
import ButtonWithTooltip from "../ButtonWithTooltip";
import DealIcon from "../icons/DealIcon";
import PortfolioIcon from "../icons/PortfolioIcon";
import OnMapSpinner from "../spinners/OnMapSpinner";
import DealFields from "./DealFields";
import DealSatelliteScreenshot from "./DealSatelliteScreenshot";
import DealScreenshotWarningModal from "./DealScreenshotWarningModal";
import DealPortfolioPopup from "./portfolio/DealPortfolioPopup";

function DealEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const updateDeal = useDealUpdate();
    const deleteDeal = useDealDelete();
    const validateDeal = useValidateDeal();
    const pipedriveStore = usePipedriveStore();
    const drawMapStore = useDrawMapStore();
    const mapMarkerStore = useMapMarkerStore();
    const deal = useMemo(() => computed(() => pipedriveStore.deals.find(d => d.id.toString() === id)), [id]).get();
    const dealStage = useMemo(() => computed(() => deal ? pipedriveStore.dealStages.find(s => s.id === deal.dealStageId) : null), [deal]).get();
    const contactPipedriveOrganization = useMemo(() => computed(() => deal && pipedriveStore.organizations.find(o => o.id === deal.contactCompanyId)), [deal]).get();
    const contactPipedrivePerson = useMemo(() => computed(() => deal && pipedriveStore.persons.find(p => p.id === deal.contactPersonId)), [deal]).get();
    const brokerPipedrivePerson = useMemo(() => computed(() => deal && pipedriveStore.persons.find(p => p.id === deal.brokerId)), [deal]).get();
    const propertyOwnerPipedrivePerson = useMemo(() => computed(() => deal && pipedriveStore.persons.find(p => p.id === deal.propertyOwnerId)), [deal]).get();

    const dealPortfolioStore = useDealPortfolioStore();
    const portfolio = useMemo(() => computed(() => deal ? dealPortfolioStore.getPortfolioForDeal(deal.id) : null), [deal, dealPortfolioStore.portfolios]).get();
    const [satelliteScreenshot, setSatelliteScreenshot] = useState<File | null>(null);
    const [showScreenshotWarning, setShowScreenshotWarning] = useState(false);
    const [isPortfolioPopupOpen, setIsPortfolioPopupOpen] = useState(false);

    const close = () => navigate("/");
    const cancel = () => navigate(`/deals/${deal!.id}`);
    useClosable(ClosableOrder.Medium, cancel);

    const formik = useFormik<IDealState>({
        initialValues: DealState.empty(),
        validate: validateDeal,
        onSubmit: async (values) => {
            await updateDeal(deal!, values, satelliteScreenshot);
            cancel();
        },
    });

    const markAsLost = async (values: IDealState) => {
        const errors = await formik.validateForm(values);
        if (Object.keys(errors).length > 0) {
            formik.setTouched(setNestedObjectValues(errors, true));
            return;
        }

        await updateDeal(deal!, values, satelliteScreenshot);
        close();
    };

    const remove = async () => {
        await deleteDeal(deal!.id);
        close();
    }

    useEffect(() => {
        if (deal) {
            pipedriveStore.setEditedDealId(deal.id);
            drawMapStore.drawDeal(deal.dealShapes);
            mapMarkerStore.draw(new GeoCoordinate(deal.lat, deal.lon));
            formik.resetForm({
                values: {
                    status: deal.status,
                    value: deal.value,
                    propertyOwnerName: propertyOwnerPipedrivePerson?.name || null,
                    name: deal.name,
                    contactPersonName: contactPipedrivePerson?.name || null,
                    contactCompanyName: contactPipedriveOrganization?.name || null,
                    dealStageId: deal.dealStageId,
                    address: deal.address,
                    city: deal.city,
                    county: deal.county,
                    state: deal.state,
                    zoning: deal.zoning,
                    buildingSize: deal.buildingSize,
                    landSizeAcres: deal.landSizeAcres,
                    usableLand: deal.usableLand,
                    dealShapeApns: deal.dealShapes.map(ds => ds.apn),
                    tenant: deal.tenant,
                    brokerName: brokerPipedrivePerson?.name || null,
                    askingPrice: deal.askingPrice,
                    nonFund: deal.nonFund,
                    dealProfile: deal.dealProfile,
                    sourcingMethod: deal.sourcingMethod,
                    dropboxLink: deal.dropboxLink,
                    assignedToUserId: deal.assignedToUserId,
                    OID: deal.OID,
                    parcelId: deal.parcelId,
                    regridId: deal.regridId,
                    labelId: deal.labelId,
                    lostReason: deal.lostReason,
                },
            });
        }

        return () => {
            pipedriveStore.setEditedDealId(null);
            drawMapStore.clear();
            mapMarkerStore.clear();
        };
    }, [deal, contactPipedriveOrganization, contactPipedrivePerson, brokerPipedrivePerson, propertyOwnerPipedrivePerson]);

    if (pipedriveStore.isDealsLoading.fetching)
        return <OnMapSpinner />;

    if (!deal)
        return null;

    return (
        <DetailsPanel type={DetailsType.DealEdit} onClose={close} controls={[
            <ButtonWithTooltip key="portfolio" title="Portfolio" className={commonStyles.navigationButton} onClick={() => setIsPortfolioPopupOpen(true)}>
                <PortfolioIcon color={portfolio ? "#25479A" : "#707070"} />
            </ButtonWithTooltip>,
        ]}>
            <div className={detailsStyles.header}>
                <div className={detailsStyles.headerGroup}>
                    <DealIcon color="#25479A" />
                    <div className={detailsStyles.title}>
                        <div>{formatPrice(deal.value)} - {dealStage!.name}</div>
                        <span className={commonStyles.primary}>{deal.name}</span>
                    </div>
                </div>
            </div>
            <div className={commonStyles.separator}></div>
            <div className={detailsStyles.body}>
                <DealFields formik={formik} onMarkAsLost={markAsLost}>
                    <DealSatelliteScreenshot onScreenshotReady={setSatelliteScreenshot} />
                </DealFields>
            </div>
            <div className={commonStyles.separator}></div>
            <div className={commonStyles.controls}>
                <div className={commonStyles.controlGroup}>
                    <EditControls
                        isStateChanged={formik.dirty || drawMapStore.isResultChanged || !mapMarkerStore.isMatch(new GeoCoordinate(deal.lat, deal.lon))}
                        isUpdateLoading={pipedriveStore.isDealsLoading.updating! || pipedriveStore.isOrganizationsLoading.creating! || pipedriveStore.isPersonsLoading.creating!}
                        onUpdate={() => satelliteScreenshot ? formik.handleSubmit() : setShowScreenshotWarning(true)}
                        onCancel={cancel}
                    />
                </div>
                <div className={commonStyles.controlGroup}>
                    <DeleteControl
                        entityName="Deal"
                        isLoading={pipedriveStore.isDealsLoading.deleting!}
                        onDelete={remove}
                    />
                </div>
            </div>
            {showScreenshotWarning && (
                <DealScreenshotWarningModal
                    onClose={() => setShowScreenshotWarning(false)}
                    onConfirm={() => { setShowScreenshotWarning(false); formik.handleSubmit(); }}
                />
            )}
            {isPortfolioPopupOpen && (
                <DealPortfolioPopup
                    dealId={deal!.id}
                    portfolio={portfolio}
                    onClose={() => setIsPortfolioPopupOpen(false)}
                />
            )}
        </DetailsPanel>
    );
}

export default observer(DealEdit);